import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import {
  setUserFromToken,
  getCurrentUser,
  getUserPermissions,
} from "../store/user";
import auth from "../services/authService";
import {
  canManageSettings,
  canManageUsers,
  canManageForms,
  canManagePackages,
  canManageEmailTemplates,
  isPatient,
  canManageApplications,
  canManageLocations,
  canManagePatients,
} from "../utils/constants";
import logo from "../Images/medsleep-logo.jpg";

const NavMenu = ({ user, ...props }) => {
  const { rolePermissions } = props;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      try {
        const token = auth.getCurrentUser();
        const { setUserFromToken } = props;
        if (token) {
          await setUserFromToken(token);
        }
      } catch (e) {}
    };
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const can = (check) => Boolean(rolePermissions) && check(rolePermissions);

  // Single source of truth for the nav links — each entry is gated by `show`.
  const links = [
    { label: "Login", to: "/login", show: !user },
    {
      label: "Dashboard",
      to: "/patient-dashboard",
      show: Boolean(user) && Boolean(rolePermissions) && isPatient(user.roleId),
    },
    {
      label: "Users",
      to: "/user-management",
      show: can(canManageUsers) || can(canManagePatients),
    },
    {
      label: "Applications",
      to: "/application-management",
      show: can(canManageApplications),
    },
    { label: "Forms", to: "/form-management", show: can(canManageForms) },
    {
      label: "Packages",
      to: "/package-management",
      show: can(canManagePackages),
    },
    {
      label: "Email Templates",
      to: "/email-template-management",
      show: can(canManageEmailTemplates),
    },
    {
      label: "Locations",
      to: "/location-management",
      show: can(canManageLocations),
    },
    { label: "Settings", to: "/settings", show: can(canManageSettings) },
    { label: "Logout", to: "/logout", show: Boolean(user) },
  ].filter((link) => link.show);

  const isActive = (to) => location.pathname === to;

  // Where the logo links: patients go to their dashboard, staff to applications.
  const homePath = !user
    ? "/login"
    : rolePermissions && isPatient(user.roleId)
    ? "/patient-dashboard"
    : "/application-management";

  const handleOpen = () => setMobileOpen(true);
  const handleClose = () => setMobileOpen(false);

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: 3,
        borderColor: "secondary.main",
        mb: 3,
      }}
    >
      <Container>
        <Toolbar disableGutters sx={{ justifyContent: "space-between", gap: 2 }}>
          <Box
            component={RouterLink}
            to={homePath}
            sx={{
              display: "none",
              "@media (min-width:1024px)": { display: "flex" },
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="MedSleep"
              sx={{ height: 40, display: "block" }}
            />
          </Box>

          {/* Desktop links */}
          <Box
            sx={{
              display: "none",
              "@media (min-width:1024px)": { display: "flex" },
              flexWrap: "nowrap",
              justifyContent: "flex-end",
              gap: 0.25,
            }}
          >
            {links.map((link) => (
              <Button
                key={link.to}
                component={RouterLink}
                to={link.to}
                disableRipple
                sx={{
                  px: 1,
                  minWidth: "auto",
                  fontSize: "0.8125rem",
                  whiteSpace: "nowrap",
                  color: isActive(link.to) ? "primary.main" : "text.primary",
                  fontWeight: isActive(link.to) ? 700 : 500,
                  borderRadius: 0,
                  borderBottom: 2,
                  borderColor: isActive(link.to)
                    ? "secondary.main"
                    : "transparent",
                  "&:hover": {
                    color: "primary.main",
                    backgroundColor: "transparent",
                    borderColor: "secondary.light",
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>

          {/* Mobile menu */}
          <Box
            sx={{
              display: "flex",
              ml: "auto",
              "@media (min-width:1024px)": { display: "none" },
            }}
          >
            <IconButton
              color="primary"
              onClick={handleOpen}
              aria-label="open navigation menu"
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={handleClose}
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                sx: {
                  width: 260,
                  maxWidth: "80vw",
                  borderTop: 3,
                  borderColor: "secondary.main",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box
                  component={RouterLink}
                  to={homePath}
                  onClick={handleClose}
                  sx={{ display: "flex", alignItems: "center", pl: 1 }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="MedSleep"
                    sx={{ height: 36, display: "block" }}
                  />
                </Box>
                <IconButton
                  color="primary"
                  onClick={handleClose}
                  aria-label="close navigation menu"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <List sx={{ py: 0 }}>
                {links.map((link) => (
                  <ListItemButton
                    key={link.to}
                    component={RouterLink}
                    to={link.to}
                    selected={isActive(link.to)}
                    onClick={handleClose}
                    sx={{
                      borderLeft: 3,
                      borderColor: isActive(link.to)
                        ? "secondary.main"
                        : "transparent",
                      "&.Mui-selected": {
                        backgroundColor: "secondary.light",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "secondary.main",
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                        borderColor: "secondary.light",
                      },
                    }}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(link.to) ? 700 : 500,
                        color: isActive(link.to)
                          ? "primary.main"
                          : "text.primary",
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Drawer>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const mapStateToProps = (state) => {
  return {
    user: getCurrentUser(state),
    rolePermissions: getUserPermissions(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ...bindActionCreators({ setUserFromToken }, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NavMenu);
